/**
 * Common Component JavaScript
 *
 * Shared behaviors for common/ templates (error_alert, success_alert, etc.).
 * Each behavior is tied to a data-* attribute declared in its template.
 */

import { Events } from '../js/utils/events.js';

let initialized = false;
let turnstileLoadPromise = null;

// ============================================================================
// Cloudflare Turnstile (signup, forgot-password and SSO-register form fragments)
// Loads Cloudflare's api.js on first use, then explicit-renders one widget per
// host and enables the form's submit button on a solved challenge.
// Template contract:
//   <div class="cf-turnstile-host" data-sitekey="..." data-submit="<submit id>">
// ============================================================================

function loadTurnstile() {
    if (turnstileLoadPromise) return turnstileLoadPromise;
    if (window.turnstile && window.turnstile.render) {
        turnstileLoadPromise = Promise.resolve();
        return turnstileLoadPromise;
    }
    turnstileLoadPromise = new Promise((resolve, reject) => {
        window.__leapfileTurnstileLoad = function () { resolve(); };
        const script = document.createElement('script');
        script.src =
            'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__leapfileTurnstileLoad&render=explicit';
        script.async = true;
        script.defer = true;
        script.onerror = () => reject(new Error('Failed to load Cloudflare Turnstile'));
        document.head.appendChild(script);
    });
    return turnstileLoadPromise;
}

function renderTurnstileInto(host) {
    if (host.dataset.rendered === 'true') return;
    host.dataset.rendered = 'true';
    const submit = host.dataset.submit
        ? document.getElementById(host.dataset.submit)
        : null;
    loadTurnstile()
        .then(() => {
            window.turnstile.render(host, {
                sitekey: host.dataset.sitekey || '',
                callback: () => { if (submit) submit.disabled = false; },
                'expired-callback': () => { if (submit) submit.disabled = true; },
                'error-callback': () => { if (submit) submit.disabled = true; },
            });
        })
        .catch((err) => {
            console.error('[LeapFILE] Turnstile failed to load:', err);
        });
}

// ============================================================================
// Clickable Rows (portal, portals, repositories and guest list pages)
// The whole row navigates, but the controls inside it must not — a checkbox
// there toggles instead. The row names what to skip, so those controls no
// longer each need a stopPropagation of their own.
// Template contract:
//   <tr data-row-href="/app/..." data-row-ignore="input">
//
// Rows that navigate through hx-get are the exception: htmx binds its own
// listener to the row, so the skip cannot live in a handler of ours. Those
// cells carry data-stop-click and get a real listener bound in activate() —
// a delegated one would fire at document capture and swallow the click for
// every other listener on the page.
// ============================================================================

function initClickableRows() {
    Events.on('click', 'row-href', (event, row) => {
        const ignore = row.dataset.rowIgnore;
        if (ignore && event.target.closest(ignore)) return;
        window.location.href = row.dataset.rowHref;
    });
}

function bindStopClick(cell) {
    if (cell.dataset.stopClickBound === 'true') return;
    cell.dataset.stopClickBound = 'true';
    cell.addEventListener('click', (event) => event.stopPropagation());
}

// ============================================================================
// Select All (portal and repository user pickers and access lists, guest
// access list, guest and portal pickers)
// One control flips every marked checkbox in its own form. Boxes the server
// renders disabled are never flipped — those rows are already granted, and
// assigning .checked would show them as a fresh selection.
// Template contract:
//   <input type="checkbox" data-select-all>            — flips to its own state
//   <a data-select-all-set="true|false">               — forces a state
//   <input type="checkbox" data-select-row>            — the boxes it flips
//
// Assigning .checked fires no change event, so a form that saves on
// hx-trigger="change" never sees a bulk flip. Only the guest portals picker
// dispatched one before, so only it asks for it with data-select-all-notify.
// ============================================================================

function flipRowBoxes(control, checked) {
    const form = control.closest('form');
    if (!form) return;
    const boxes = form.querySelectorAll('[data-select-row]:not([disabled])');
    boxes.forEach((box) => { box.checked = checked; });
    if (boxes.length > 0 && control.hasAttribute('data-select-all-notify')) {
        boxes[0].dispatchEvent(new Event('change', { bubbles: true }));
    }
}

function initSelectAll() {
    Events.on('change', 'select-all', (event, element) => {
        flipRowBoxes(element, element.checked);
    });
    Events.on('click', 'select-all-set', (event, element) => {
        event.preventDefault();
        flipRowBoxes(element, element.dataset.selectAllSet === 'true');
    });
}

// ============================================================================
// Submit On Change (the three transfer utilization period selectors)
// The selects sit in an hx-get form and refetch the page on every pick.
// requestSubmit, not submit: it fires the submit event htmx listens for.
// Template contract: <select data-submit-on-change>
// ============================================================================

function initSubmitOnChange() {
    Events.on('change', 'submit-on-change', (event, element) => {
        if (element.form) element.form.requestSubmit();
    });
}

// ============================================================================
// Color Picker (components/color_picker.jinja2, used throughout branding)
// The swatch and the hex field mirror each other, and the field paints its
// own background so the value is readable at a glance. They find each other
// through their shared wrapper rather than by id.
// Template contract:
//   <div data-color-picker>
//     <input type="color" data-color-swatch>
//     <input type="text" data-color-text>
//   </div>
// ============================================================================

function initColorPicker() {
    Events.on('change', 'color-swatch', (event, swatch) => {
        const wrapper = swatch.closest('[data-color-picker]');
        const text = wrapper && wrapper.querySelector('[data-color-text]');
        if (!text) return;
        text.value = swatch.value;
        text.style.backgroundColor = swatch.value;
    });
    Events.on('input', 'color-text', (event, text) => {
        const wrapper = text.closest('[data-color-picker]');
        const swatch = wrapper && wrapper.querySelector('[data-color-swatch]');
        if (/^#[0-9A-Fa-f]{6}$/.test(text.value)) {
            if (swatch) swatch.value = text.value;
            text.style.backgroundColor = text.value;
        } else if (!text.value) {
            // A half-typed hex keeps the last painted colour, as it always has.
            text.style.backgroundColor = '';
        }
    });
}

// ============================================================================
// Small Markup Behaviours
// The one-liners that used to sit in an attribute each. Every one of them
// keeps exactly what its handler did.
// Template contract:
//   <a href="#" data-prevent-default>        an htmx link that must not jump to #
//   <img data-nav="https://...">             go to that URL on click
//   <button data-nav-back>                   go back one history entry
//   <button data-set-field="x" data-set-value="y">  set a field before submit
//   <button data-goto-logs-page data-offset="20">    log-page pagination
//   <textarea data-submit-on-enter>          submit on Enter, newline on Shift+Enter
//   <span data-disclosure-toggle>            toggle .open on itself and the next sibling
//   <input data-readonly-until-focus>        drop readonly once focused
// ============================================================================

function initMarkupBehaviours() {
    Events.on('click', 'prevent-default', (event) => {
        event.preventDefault();
    });
    Events.on('click', 'nav', (event, element) => {
        window.location.href = element.dataset.nav;
    });
    Events.on('click', 'nav-back', () => {
        window.history.back();
    });
    // Addressed by name off the button's own form: these fields carry an id
    // that differs from their name (addAnother vs add_another), and the name
    // is what the backend reads.
    Events.on('click', 'set-field', (event, element) => {
        const field = element.form && element.form.elements[element.dataset.setField];
        if (field) field.value = element.dataset.setValue;
    });
    // Pagination on the portal and repository log pages. Both name the same
    // two ids, which is what the removed gotoLogsPage() looked up.
    Events.on('click', 'goto-logs-page', (event, element) => {
        const form = document.getElementById('logs-form');
        const startInput = document.getElementById('pagination-start');
        if (!form || !startInput) return;
        startInput.value = element.dataset.offset;
        form.requestSubmit();
    });
    Events.on('keydown', 'submit-on-enter', (event, element) => {
        if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
        if (!element.form) return;
        event.preventDefault();
        element.form.requestSubmit();
    });
    Events.on('click', 'disclosure-toggle', (event, element) => {
        element.classList.toggle('open');
        if (element.nextElementSibling) {
            element.nextElementSibling.classList.toggle('open');
        }
    });
    // Rendered readonly so mobile keyboards and password managers leave the
    // field alone until the user actually taps it.
    Events.on('focus', 'readonly-until-focus', (event, element) => {
        element.removeAttribute('readonly');
    });
}

// ============================================================================
// Copy To Clipboard (CSV import examples, generated API key)
// Four pages carried near-identical copies of this. The button names the
// element to copy from and reads it out of the DOM, rather than the value
// being templated into a script — which is what the api-keys block already
// did deliberately.
// Template contract:
//   <button data-copy="<id>" data-copy-reveals="<id>"
//           data-copy-enables="<id>">Copy</button>
// ============================================================================

function initCopyToClipboard() {
    Events.on('click', 'copy', (event, element) => {
        const source = document.getElementById(element.dataset.copy);
        if (!source) return;
        if (!element.dataset.copyLabel) {
            element.dataset.copyLabel = element.textContent.trim();
        }
        const restore = () => setTimeout(() => {
            element.textContent = element.dataset.copyLabel;
        }, 1500);
        navigator.clipboard
            .writeText(source.textContent.trim())
            .then(() => {
                element.textContent = 'Copied';
                // The API key list stays dimmed until the key is safely copied.
                const reveals = element.dataset.copyReveals
                    ? document.getElementById(element.dataset.copyReveals)
                    : null;
                if (reveals) reveals.classList.remove('opacity-50');
                // The observation key must be copied before the flow moves on.
                const enables = element.dataset.copyEnables
                    ? document.getElementById(element.dataset.copyEnables)
                    : null;
                if (enables) enables.disabled = false;
                restore();
            })
            .catch(() => {
                element.textContent = 'Failed';
                restore();
            });
    });
}

// ============================================================================
// Timezone Offset Input (the SSO callback form)
// The form posts the browser's offset alongside the CAS ticket. It used to
// come from a document.write that ran while the form was being parsed.
//
// The form must not fire on hx-trigger="load": htmx registers its ready
// handler when the bundle imports it, so it processes the DOM and sends the
// request before this module's DOMContentLoaded handler ever runs, and the
// offset would travel as 0. The form waits for tz-ready instead, which is
// dispatched here once the value is in. htmx binds that trigger during its
// own pass, which is the pass that already finished by the time we dispatch.
// Template contract:
//   <form hx-trigger="tz-ready">
//     <input type="hidden" name="gmt_offset" value="0" data-tz-sign="-1">
// ============================================================================

function applyTimezoneOffset(input) {
    if (input.dataset.tzApplied === 'true') return;
    input.dataset.tzApplied = 'true';
    input.value = Number(input.dataset.tzSign) * new Date().getTimezoneOffset();
    const form = input.closest('form');
    if (form) form.dispatchEvent(new CustomEvent('tz-ready'));
}

// ============================================================================
// Whitespace-free Fields (the add-user email input)
// Template contract: <input data-strip-whitespace>
// ============================================================================

function initStripWhitespace() {
    Events.on('input', 'strip-whitespace', (event, element) => {
        element.value = element.value.replace(/\s+/g, '');
    });
}

// ============================================================================
// Auto-submitting Forms (the unsubscribe landing page)
// The page exists only to post; the <noscript> button covers the rest.
// Template contract: <form data-autosubmit>
// ============================================================================

function autosubmit(form) {
    if (form.dataset.autosubmitted === 'true') return;
    form.dataset.autosubmitted = 'true';
    form.submit();
}

// ============================================================================
// OOB Alert Scroll (common/error_alert.jinja2, common/success_alert.jinja2)
// Scrolls OOB alerts into view so users don't miss them.
// Template contract: data-error-alert, data-success-alert attributes.
// ============================================================================

function initAlertScroll() {
    document.addEventListener('htmx:afterSettle', (e) => {
        const selector = '[data-error-alert], [data-success-alert]';
        const alert = e.target.matches(selector) ? e.target : e.target.querySelector(selector);
        if (alert) {
            alert.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    });
}

// ============================================================================
// Dirty Form Tracking (Settings card commit boundary)
// A [data-dirty-form] is a single commit unit. Its [data-dirty-footer] —
// containing the Save/Cancel pair — is hidden until the form becomes dirty,
// then revealed. Cancel reverts fields to the captured initial values.
// On successful save, the new values become the pristine baseline.
//
// Template contract:
//   <form data-dirty-form ...>
//       <input name="...">
//       <div data-dirty-footer hidden>
//           <button data-dirty-submit type="submit">Save</button>
//           <button data-dirty-cancel type="button">Cancel</button>
//       </div>
//   </form>
// ============================================================================

function getFieldValue(field) {
    if (field.type === 'checkbox' || field.type === 'radio') {
        return field.checked ? '1' : '0';
    }
    return field.value;
}

function setFieldValue(field, value) {
    if (field.type === 'checkbox' || field.type === 'radio') {
        field.checked = value === '1';
    } else {
        field.value = value;
    }
}

function captureInitialValues(form) {
    const initial = {};
    form.querySelectorAll('input, textarea, select').forEach((field) => {
        if (!field.name) return;
        // Keyed by name+type+value so same-named radios stay distinct
        const key = field.type === 'radio' ? `${field.name}::${field.value}` : field.name;
        initial[key] = getFieldValue(field);
    });
    form._dirtyInitial = initial;
}

function revertToInitialValues(form) {
    const initial = form._dirtyInitial;
    if (!initial) return;
    form.querySelectorAll('input, textarea, select').forEach((field) => {
        if (!field.name) return;
        const key = field.type === 'radio' ? `${field.name}::${field.value}` : field.name;
        if (key in initial) setFieldValue(field, initial[key]);
    });
}

function isFormDirty(form) {
    const initial = form._dirtyInitial;
    if (!initial) return false;
    const fields = form.querySelectorAll('input, textarea, select');
    for (const field of fields) {
        if (!field.name) continue;
        const key = field.type === 'radio' ? `${field.name}::${field.value}` : field.name;
        if (getFieldValue(field) !== initial[key]) return true;
    }
    return false;
}

function updateDirtyState(form) {
    const dirty = isFormDirty(form);
    form.querySelectorAll('[data-dirty-submit]').forEach((btn) => {
        btn.disabled = !dirty;
    });
    form.querySelectorAll('[data-dirty-footer]').forEach((footer) => {
        if (dirty) {
            footer.removeAttribute('hidden');
        } else {
            footer.setAttribute('hidden', '');
        }
    });
    // Stale save/error feedback should not linger once the user starts editing.
    form.querySelectorAll('[data-dirty-status]').forEach((el) => {
        el.hidden = dirty;
    });
}

function initializeDirtyForm(form) {
    captureInitialValues(form);
    updateDirtyState(form);
}

function initDirtyFormTracking() {
    // Initialize any dirty forms present on initial page load
    document.querySelectorAll('[data-dirty-form]').forEach(initializeDirtyForm);

    // Initialize dirty forms brought in via HTMX swaps
    document.addEventListener('htmx:afterSettle', (e) => {
        const scope = e.target;
        if (!scope || typeof scope.querySelectorAll !== 'function') return;
        const forms = scope.matches && scope.matches('[data-dirty-form]')
            ? [scope]
            : scope.querySelectorAll('[data-dirty-form]');
        forms.forEach(initializeDirtyForm);
    });

    // Re-evaluate dirty state as the user edits
    const onFieldChange = (e) => {
        const form = e.target.closest('[data-dirty-form]');
        if (form) updateDirtyState(form);
    };
    document.addEventListener('input', onFieldChange, true);
    document.addEventListener('change', onFieldChange, true);

    // Cancel reverts the form's fields to their captured initial values.
    document.addEventListener('click', (e) => {
        const cancel = e.target.closest('[data-dirty-cancel]');
        if (!cancel) return;
        const form = cancel.closest('[data-dirty-form]');
        if (!form) return;
        e.preventDefault();
        revertToInitialValues(form);
        updateDirtyState(form);
    });

    // Reset to pristine only when the server signals a real save with the
    // `X-Dirty-Form-Saved: 1` response header. Validation errors ride back
    // as HTTP 200 + OOB alert (so HTMX swaps the alert into the result slot)
    // and would otherwise be indistinguishable from a real save here.
    document.addEventListener('htmx:afterRequest', (e) => {
        const form = e.detail && e.detail.elt
            ? (e.detail.elt.matches('[data-dirty-form]') ? e.detail.elt : e.detail.elt.closest('[data-dirty-form]'))
            : null;
        if (!form) return;
        if (!e.detail.successful) return;
        const xhr = e.detail.xhr;
        if (xhr && xhr.getResponseHeader('X-Dirty-Form-Saved') === '1') {
            initializeDirtyForm(form);
        }
    });
}

// ============================================================================
// User Menu Dropdown (app_shell_template.jinja2)
// Toggles a menu panel from the header avatar button. Closes on outside
// click, Escape, or selecting a menu item. Keeps aria-expanded in sync.
// Template contract:
//   <button data-user-menu-trigger aria-expanded="false">...</button>
//   <div data-user-menu-panel hidden>...</div>
// ============================================================================

function closeUserMenu(trigger, panel) {
    panel.setAttribute('hidden', '');
    trigger.setAttribute('aria-expanded', 'false');
}

function openUserMenu(trigger, panel) {
    panel.removeAttribute('hidden');
    trigger.setAttribute('aria-expanded', 'true');
}

function initUserMenu() {
    document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-user-menu-trigger]');
        const panel = document.querySelector('[data-user-menu-panel]');
        if (!panel) return;

        if (trigger) {
            e.preventDefault();
            const isOpen = !panel.hasAttribute('hidden');
            if (isOpen) {
                closeUserMenu(trigger, panel);
            } else {
                openUserMenu(trigger, panel);
            }
            return;
        }

        const triggerEl = document.querySelector('[data-user-menu-trigger]');
        if (!triggerEl || panel.hasAttribute('hidden')) return;

        // Click on a menu item closes the menu
        if (e.target.closest('[role="menuitem"]')) {
            closeUserMenu(triggerEl, panel);
            return;
        }

        // Click outside both trigger and panel closes the menu
        if (!panel.contains(e.target)) {
            closeUserMenu(triggerEl, panel);
        }
    }, true);

    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const panel = document.querySelector('[data-user-menu-panel]');
        const trigger = document.querySelector('[data-user-menu-trigger]');
        if (!panel || !trigger || panel.hasAttribute('hidden')) return;
        closeUserMenu(trigger, panel);
        trigger.focus();
    });
}

// ============================================================================
// Dark Mode Toggle (shell_footer/shell_footer_template.jinja2)
// Click [data-theme-toggle] to flip .dark on <html> and persist to localStorage.
// The icon swap happens via Tailwind dark: modifiers in the template.
// The no-flash init runs in head (see app_sidebar_shell_layout.jinja2) so the
// .dark class is already correct before this listener attaches.
// ============================================================================

function initThemeToggle() {
    document.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-theme-toggle]');
        if (!toggle) return;
        e.preventDefault();
        const root = document.documentElement;
        const nextDark = !root.classList.contains('dark');
        root.classList.toggle('dark', nextDark);
        try {
            localStorage.setItem('leapfile.theme', nextDark ? 'dark' : 'light');
        } catch (err) {
            // localStorage unavailable (private mode, etc.) — toggle still works
            // for this page but won't persist.
        }
    });
}

// ============================================================================
// UI Preview Toggle (legacy session_header + new shell_footer)
// Two affordances flip the leapfile.ui_preview cookie and reload the page:
//   [data-ui-preview-enter] — sparkles icon in legacy session_header
//   [data-ui-preview-exit]  — "Exit preview" entry in new shell footer dropdown
// Cookie persists for 1 year; cleared by setting Max-Age=0.
// ============================================================================

function setUiPreviewCookie(enabled) {
    const value = enabled ? '1' : '0';
    const maxAge = enabled ? 60 * 60 * 24 * 365 : 0;  // 1 year on, 0 = delete
    document.cookie = `leapfile.ui_preview=${value}; path=/; max-age=${maxAge}; samesite=lax`;
}

function initUiPreviewToggle() {
    document.addEventListener('click', (e) => {
        const enter = e.target.closest('[data-ui-preview-enter]');
        if (enter) {
            e.preventDefault();
            setUiPreviewCookie(true);
            window.location.reload();
            return;
        }
        const exit = e.target.closest('[data-ui-preview-exit]');
        if (exit) {
            e.preventDefault();
            setUiPreviewCookie(false);
            window.location.reload();
        }
    });
}

// ============================================================================
// Public init
// ============================================================================

function activate(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('.cf-turnstile-host').forEach(renderTurnstileInto);
    root.querySelectorAll('[data-stop-click]').forEach(bindStopClick);
    root.querySelectorAll('[data-tz-sign]').forEach(applyTimezoneOffset);
    root.querySelectorAll('[data-autosubmit]').forEach(autosubmit);
}

export function initCommon() {
    if (initialized) return;
    initialized = true;

    initAlertScroll();
    initDirtyFormTracking();
    initUserMenu();
    initThemeToggle();
    initUiPreviewToggle();
    initClickableRows();
    initSelectAll();
    initSubmitOnChange();
    initColorPicker();
    initMarkupBehaviours();
    initCopyToClipboard();
    initStripWhitespace();

    activate(document);


    // Form fragments self-replace on validation failure, bringing in a fresh
    // widget host that needs activating again. The event fires on the
    // swapped-in element, so scope off event.target — detail.target is the
    // replaced element, already out of the document.
    document.addEventListener('htmx:afterSwap', (event) => {
        activate(event.target);
    });
}
